import type {
  CustomNode,
  GroupedNodes,
  InputValue,
  NodeStruct,
} from '@/types/node-types';
import { useNodeStore } from '@/stores/node-store';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import {
  findJudgmentGateStructs,
  stampJudgmentGate,
  type JudgmentGateStructs,
} from '@/pages/workflow/helper/judgment-gate-stamp';
import { canBeRepeated, nodeLabel } from '@/pages/workflow/helper/repeat-loop';
import { defaultFeedbackSlot } from '@/pages/workflow/helper/repeat-author';
import { REPEAT_NODE_ID } from '@/pages/workflow/helper/repeat-control';
import { VALIDATE_NODE_ID } from '@/pages/workflow/helper/functional-control-flow';

/**
 * One-click patterns on a selected node, built from the existing stamps and
 * the Phase 3 loop author. Each leaves a graph the editor's own rules accept
 * and opens the panel the person will want next.
 */

const STAMP_H_GAP = 380;
const NODE_HEIGHT_GAP = 40;

const catalogOf = (grouped: GroupedNodes): NodeStruct[] =>
  Object.values(grouped).flat();

const primaryOutputPort = (node: CustomNode) => {
  const ports = node.data.struct.output_ports ?? [];
  return (
    ports.find((port) => port.name === 'output') ??
    ports.find((port) => port.name === 'result') ??
    ports.find((port) => port.name === 'value') ??
    ports[0] ??
    null
  );
};

/**
 * Select the node and open its panel. Deferred one tick past the context
 * menu's own close handling, which deselects whatever was selected when the
 * menu opened on a node.
 */
const focusNode = (nodeId: string) => {
  const focus = () => {
    useNodeStore.getState().selectNode(nodeId);
    const panel = useWorkflowPanelStore.getState();
    panel.openWorkflowPanel('configure');
    panel.setConsolePanelOpen(true);
  };
  focus();
  if (typeof window !== 'undefined') window.setTimeout(focus, 60);
};

// --- Add a decision gate after a node ----------------------------------------

export interface DecisionGateAvailability {
  available: boolean;
  reason?: string;
  structs?: JudgmentGateStructs;
}

/** Whether "Add a decision gate" can run after this node. */
export const decisionGateAvailability = (
  grouped: GroupedNodes,
  node: CustomNode | undefined,
): DecisionGateAvailability => {
  const structs = findJudgmentGateStructs(grouped);
  if (!structs) {
    return {
      available: false,
      reason: 'Classify, Validate or Switch is not available in this org.',
    };
  }
  if (!node) return { available: false, reason: 'Select one node first.' };
  if (!primaryOutputPort(node)) {
    return {
      available: false,
      reason: `${nodeLabel(node)} has no output to judge.`,
    };
  }
  return { available: true, structs };
};

export interface DecisionGateResult {
  classifyId: string;
  validateId: string;
  switchId: string;
}

/**
 * Stamp Classify → Validate → Switch to the right of `node` and feed the
 * node's output into the classifier's Subject, then open the classifier so
 * the labels and criteria are the next thing the person edits.
 */
export const addDecisionGateAfter = (
  grouped: GroupedNodes,
  nodeId: string,
): DecisionGateResult | null => {
  const store = useNodeStore.getState();
  const node = store.nodes.find((candidate) => candidate.id === nodeId);
  const availability = decisionGateAvailability(grouped, node);
  if (!node || !availability.available || !availability.structs) return null;
  const position = {
    x: node.position.x + STAMP_H_GAP,
    y: node.position.y,
  };
  const stamped = stampJudgmentGate(availability.structs, position);
  if (!stamped) return null;

  const sourcePort = primaryOutputPort(node);
  const subjectPort = availability.structs.classify.params?.find(
    (param) => param.field_id === 'subject',
  )?.bindable_config?.input_port;
  if (sourcePort && subjectPort) {
    useNodeStore.getState().onConnect({
      source: node.id,
      sourceHandle: sourcePort.id,
      target: stamped.classifyId,
      targetHandle: subjectPort.id,
    });
  }
  focusNode(stamped.classifyId);
  return stamped;
};

// --- Make this part repeat until it passes -----------------------------------

export interface RepeatAvailability {
  available: boolean;
  reason?: string;
}

/** Whether "Make this part repeat until it passes" can wrap this node. */
export const repeatAvailability = (
  grouped: GroupedNodes,
  node: CustomNode | undefined,
): RepeatAvailability => {
  const catalog = catalogOf(grouped);
  const validate = catalog.find((entry) => entry.node_id === VALIDATE_NODE_ID);
  const repeat = catalog.find((entry) => entry.node_id === REPEAT_NODE_ID);
  if (!validate || !repeat) {
    return {
      available: false,
      reason: 'Validate or Repeat Until is not available in this org.',
    };
  }
  if (!node) return { available: false, reason: 'Select one node first.' };
  if (!canBeRepeated(node.data.struct, catalog)) {
    return {
      available: false,
      reason: `${nodeLabel(node)} cannot be repeated; only an Agent, a Lambda step or a Map Item can.`,
    };
  }
  if (!defaultFeedbackSlot(node.data.struct, catalog)) {
    return {
      available: false,
      reason: `${nodeLabel(node)} has no input that can take the judge's feedback.`,
    };
  }
  if (!primaryOutputPort(node)) {
    return {
      available: false,
      reason: `${nodeLabel(node)} has no output to judge.`,
    };
  }
  return { available: true };
};

export interface RepeatStampResult {
  validateId: string;
  carrierId: string;
}

/** A starter check the person edits: passes once the step reports `done`. */
export const STARTER_LOOP_ASSERTION = {
  code: 'done',
  message: 'The step has not said it is done yet',
  when: {
    op: 'and',
    args: [
      { op: 'exists', path: { op: 'path', path: ['value', 'done'] } },
      {
        op: 'eq',
        left: { op: 'path', path: ['value', 'done'] },
        right: { op: 'literal', value: true },
      },
    ],
  },
};

/**
 * Insert a Validate judge and a Repeat Until after `node`, wire step →
 * judge → carrier and the back edge through the loop author, and open the
 * loop panel. The judge starts in collect mode with one rule to edit.
 */
export const makeRepeatUntilPasses = (
  grouped: GroupedNodes,
  nodeId: string,
): RepeatStampResult | null => {
  const store = useNodeStore.getState();
  const node = store.nodes.find((candidate) => candidate.id === nodeId);
  if (!node || !repeatAvailability(grouped, node).available) return null;
  const catalog = catalogOf(grouped);
  const validate = catalog.find((entry) => entry.node_id === VALIDATE_NODE_ID)!;
  const repeat = catalog.find((entry) => entry.node_id === REPEAT_NODE_ID)!;

  const [validateId, carrierId] = store.addNodes([
    {
      struct: validate,
      position: { x: node.position.x + STAMP_H_GAP, y: node.position.y },
    },
    {
      struct: repeat,
      position: {
        x: node.position.x + 2 * STAMP_H_GAP,
        y: node.position.y + NODE_HEIGHT_GAP,
      },
    },
  ]);
  const fresh = useNodeStore.getState();
  fresh.handleParameterChange(validateId, 'mode', 'collect');
  fresh.handleParameterChange(validateId, 'assertions', [
    STARTER_LOOP_ASSERTION,
  ] as unknown as InputValue);
  fresh.handleParameterChange(validateId, 'error_code', 'not_done_yet');
  fresh.handleParameterChange(
    validateId,
    'error_message',
    'The step has not produced an acceptable result yet',
  );

  useNodeStore.getState().applyLoopSentence(carrierId, {
    entryId: node.id,
    judgeId: validateId,
    condition: {
      kind: 'rule',
      field: ['is_valid'],
      op: 'eq',
      value: true,
      guardMissing: false,
    },
    maxIterations: 3,
    feedbackPath: ['value'],
    feedbackSlot: defaultFeedbackSlot(node.data.struct, catalog),
  });
  focusNode(carrierId);
  return { validateId, carrierId };
};
